import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import {
  afterFailedFetch,
  afterFetch,
  beforeFetch
} from "../../../redux/shared/paged-list.ts"
import {
  copyPhaseForProjectType,
  createPhaseForProjectType,
  deletePhaseForProjectType,
  partialUpdatePhase
} from "./templates-phases.thunks.ts"
import {
  copyProjectType,
  createProjectType,
  deleteProjectType,
  fetchProjectTypeDetails,
  fetchProjectTypes,
  partialUpdateProjectType,
  updateProjectTypeAndPhase
} from "./templates-project-types.thunks.ts"
import { SaveResultType, type TemplatesState } from "./templates.types.ts"

// Initial State
const initialState: TemplatesState = {
  fetching: false,
  requestId: null,
  saveFetching: false,
  saveResult: null,

  projectTypeDetails: null,
  selectedPhase: null,

  projectTypes: {
    filters: {},
    result: {
      count: 0,
      next: null,
      results: []
    },

    autocompleteList: []
  }
}

// Slice
const templatesSlice = createSlice({
  name: "templates",
  initialState,
  reducers: {
    setTemplatesProp: (
      state,
      action: PayloadAction<{
        key:
          | keyof TemplatesState
          | `projectTypes.${keyof TemplatesState["projectTypes"]}`
        value: any
      }>
    ) => {
      const { key, value } = action.payload

      if (key.includes(".")) {
        const [parent, child] = key.split(".") as [
          keyof TemplatesState,
          keyof TemplatesState["projectTypes"]
        ]
        if (parent === "projectTypes") {
          ;(state[parent][child] as any) = value
        }
        return
      }

      if (value && typeof value === "object" && !Array.isArray(value)) {
        ;(state[key as keyof TemplatesState] as any) = {
          ...(state[key as keyof TemplatesState] as Record<string, unknown>),
          ...value
        }
      } else {
        ;(state[key as keyof TemplatesState] as any) = value
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Project Types
      .addCase(fetchProjectTypes.pending, (state, action) => {
        state.fetching = true
        state.requestId = action.meta.requestId
        state.projectTypes.result = beforeFetch(
          state.projectTypes.result,
          initialState.projectTypes.result,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchProjectTypes.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.projectTypes.result = afterFetch(
          state.projectTypes.result,
          action.payload,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchProjectTypes.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.projectTypes.result = afterFailedFetch(
          state.projectTypes.result,
          initialState.projectTypes.result,
          action.meta?.arg?.isMore
        )
      })

      // Create Project Type
      .addCase(createProjectType.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(createProjectType.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = { type: SaveResultType.PROJECT_TYPE_CREATE }
      })
      .addCase(createProjectType.rejected, (state) => {
        state.saveFetching = false
      })

      // Copy Project Type
      .addCase(copyProjectType.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(copyProjectType.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { type: SaveResultType.PROJECT_TYPE_CREATE }
      })
      .addCase(copyProjectType.rejected, (state) => {
        state.saveFetching = false
      })

      // Fetch Project Type Details
      .addCase(fetchProjectTypeDetails.pending, (state) => {
        state.fetching = true
      })
      .addCase(fetchProjectTypeDetails.fulfilled, (state, action) => {
        const projectType = action?.payload
        const firstPhase = projectType?.phases?.[0] || null

        state.fetching = false
        state.projectTypeDetails = projectType
        state.selectedPhase = firstPhase
      })
      .addCase(fetchProjectTypeDetails.rejected, (state) => {
        state.fetching = false
      })

      // Partial Update Project Type
      .addCase(partialUpdateProjectType.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(partialUpdateProjectType.fulfilled, (state, action) => {
        state.projectTypeDetails = {
          ...state?.projectTypeDetails,
          ...action.payload
        }
        state.saveFetching = false
        state.saveResult = { type: SaveResultType.PROJECT_TYPE_UPDATE }
      })
      .addCase(partialUpdateProjectType.rejected, (state) => {
        state.saveFetching = false
      })

      // UpdateProjectTypeAndPhase
      .addCase(updateProjectTypeAndPhase.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateProjectTypeAndPhase.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = { type: SaveResultType.PROJECT_TYPE_UPDATE }
        state.projectTypeDetails = action.payload

        if (state.selectedPhase) {
          const updatedPhase = action?.payload?.phases?.find(
            (p) => p.id === state?.selectedPhase?.id
          )

          if (updatedPhase) state.selectedPhase = updatedPhase
        }
      })
      .addCase(updateProjectTypeAndPhase.rejected, (state) => {
        state.saveFetching = false
      })

      // Delete Project Type
      .addCase(deleteProjectType.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(deleteProjectType.fulfilled, (state, action) => {
        state.saveFetching = false
      })
      .addCase(deleteProjectType.rejected, (state) => {
        state.saveFetching = false
      })

      // Partial Update Project Type Phase
      .addCase(partialUpdatePhase.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(partialUpdatePhase.fulfilled, (state, action) => {
        if (state?.projectTypeDetails) {
          state.projectTypeDetails.phases =
            state?.projectTypeDetails?.phases?.map((phase) => {
              if (phase.id === action.payload.id) {
                return {
                  ...phase,
                  ...action.payload
                }
              }
              return phase
            })
        }

        state.saveFetching = false
        state.saveResult = { type: SaveResultType.PHASE_UPDATE }
      })
      .addCase(partialUpdatePhase.rejected, (state) => {
        state.saveFetching = false
      })

      // Create Phase
      .addCase(createPhaseForProjectType.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(createPhaseForProjectType.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = { type: SaveResultType.PHASE_CREATE }

        const projectType = state.projectTypeDetails

        if (projectType) {
          projectType.phases = [...(projectType?.phases || []), action.payload]
        }
      })
      .addCase(createPhaseForProjectType.rejected, (state) => {
        state.saveFetching = false
      })

      // Copy Phase
      .addCase(copyPhaseForProjectType.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(copyPhaseForProjectType.fulfilled, (state, action) => {
        state.saveFetching = false

        const projectType = state.projectTypeDetails

        if (projectType) {
          if (Array.isArray(action.payload)) {
            projectType.phases = action.payload
            // state.selectedPhase = action.payload?.[0]
          } else {
            projectType.phases = [
              ...(projectType?.phases || []),
              action.payload
            ]
            state.selectedPhase = action.payload
          }
        }
      })
      .addCase(copyPhaseForProjectType.rejected, (state) => {
        state.saveFetching = false
      })

      // Delete Phase
      .addCase(deletePhaseForProjectType.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(deletePhaseForProjectType.fulfilled, (state, action) => {
        state.saveFetching = false

        const projectType = state.projectTypeDetails

        if (projectType) {
          projectType.phases = projectType?.phases?.filter(
            (phase) => phase.id !== action.payload
          )
        }

        state.selectedPhase = null
      })
      .addCase(deletePhaseForProjectType.rejected, (state) => {
        state.saveFetching = false
      })
  }
})

export const { setTemplatesProp } = templatesSlice.actions

export default templatesSlice
